
import { useState } from "react";
import { VacationCard } from "./VacationCard";
import { VacationCardSkeleton } from "./VacationCardSkeleton";
import { VacationMascot } from "./VacationMascot";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Users, PartyPopper } from "lucide-react";
import { useJongerenreizenData } from "./jongerenreizen/useJongerenreizenData";
import { JongerenreizenFilters } from "./jongerenreizen/JongerenreizenFilters";
import { useIsMobile } from "@/hooks/use-mobile";

interface JongerenreizenProps {
  onBack: () => void;
}

export const Jongerenreizen = ({ onBack }: JongerenreizenProps) => {
  const isMobile = useIsMobile();
  const [searchTerm, setSearchTerm] = useState("");
  const [countryFilter, setCountryFilter] = useState("all");
  const [priceFilter, setPriceFilter] = useState("all");
  
  const { vacations, loading, countries } = useJongerenreizenData(searchTerm, countryFilter, priceFilter);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
        <Button 
          onClick={onBack}
          variant="outline" 
          className="flex items-center gap-2 border-orange-300 hover:bg-orange-50"
        >
          <ArrowLeft className="h-4 w-4" />
          {!isMobile && "Terug"}
        </Button>
        
        <div className="flex items-center gap-4 sm:gap-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
              <Users className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Jongerenreizen
              </h1>
              <p className="text-gray-600 text-sm sm:text-base">
                Feest, avontuur en nieuwe vrienden!
              </p>
            </div>
          </div>
          <div className="flex-shrink-0">
            <VacationMascot size="small" />
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-200">
        <div className="flex items-center gap-3 mb-3">
          <PartyPopper className="h-6 w-6 text-purple-600" />
          <h2 className="text-xl font-bold text-purple-800">
            Ontdek de beste jongerenreizen!
          </h2>
        </div>
        <p className="text-purple-700 mb-4">
          Van strandfeesten tot avontuurlijke groepsreizen - vind jouw perfecte jongerenvakantie vol nieuwe vrienden en onvergetelijke herinneringen!
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
            <span className="text-purple-700">Groepsreizen speciaal voor jongeren</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-pink-500 rounded-full"></span>
            <span className="text-purple-700">Feest- en uitgaansbestemmingen</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
            <span className="text-purple-700">Avontuurlijke activiteiten</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-pink-500 rounded-full"></span>
            <span className="text-purple-700">Nieuwe vrienden maken</span>
          </div>
        </div>
      </div>

      <JongerenreizenFilters 
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        countryFilter={countryFilter}
        setCountryFilter={setCountryFilter}
        priceFilter={priceFilter}
        setPriceFilter={setPriceFilter}
        countries={countries}
      />

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {Array.from({ length: 8 }).map((_, index) => (
            <VacationCardSkeleton key={index} />
          ))}
        </div>
      ) : vacations.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-24 h-24 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Users className="h-12 w-12 text-purple-500" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-4">
            Geen jongerenreizen gevonden
          </h3>
          <p className="text-gray-600 mb-8 max-w-md mx-auto">
            Probeer je filters aan te passen of kom binnenkort terug - we breiden ons aanbod regelmatig uit!
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <p className="text-gray-600">
              {vacations.length} jongerenrei{vacations.length === 1 ? 's' : 'zen'} gevonden
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {vacations.map((vacation, index) => (
              <VacationCard
                key={`${vacation.id || vacation.name}-${index}`}
                vacation={vacation}
                user={null}
                isFavorite={false}
                onFavoriteChange={() => {}}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
